import { AsyncDirective, directive } from 'lit/async-directive.js'
import { ElementPart, PartType } from 'lit/directive.js'
import { EMPTY, Observable, Subject, animationFrameScheduler, combineLatest, fromEvent, merge, timer } from 'rxjs'
import { auditTime, distinctUntilChanged, filter, map, startWith, switchMap, takeUntil, tap } from 'rxjs/operators'
import { theme } from '@schmancy/theme/theme.service'

/**
 * Nearest `[data-fill-boundary]` at or above `from` in the FLATTENED tree, or
 * null when nothing in the ancestry is marked — the backward-compatible case
 * every ordinary surface takes, where the window is the boundary.
 *
 * That one attribute says "this box is the viewport for everything inside it",
 * and both layout primitives read it here: `fill()` sizes to it instead of the
 * window, `show()` mounts inside it and resolves every sheet decision against
 * it. A device bezel or embedded canvas is declared once and both obey.
 *
 * `assignedSlot` is tried before `parentElement`: a slotted node's
 * parentElement is the shadow host, which leaps over the shadow tree it renders
 * inside — exactly where a frame's own clipping box (a div wrapping its
 * `<slot>`) lives. The host hop then continues the climb past the root.
 */
export function findFillBoundary(from: Element | null | undefined): HTMLElement | null {
	let node: Element | null = from ?? null
	while (node) {
		if (node instanceof HTMLElement && node.hasAttribute('data-fill-boundary')) return node
		const root = node.getRootNode()
		node = node.assignedSlot ?? node.parentElement ?? (root instanceof ShadowRoot ? root.host : null)
	}
	return null
}

/**
 * Anchors an element to the viewport — or to the nearest `[data-fill-boundary]`
 * ancestor, so a viewport-anchored view renders correctly inside a smaller
 * mount with no iframe — in both dimensions via inline height/width;
 * `overflow: hidden` contains children unless the element already declares its
 * own scrolling, so `${fill()} ${overflowWithin()}` on one box is a
 * viewport-height scroll region and an inner region is `overflowWithin()` on
 * its own element.
 *
 * Reactive to viewport/ResizeObserver/orientation/theme-offset changes,
 * frame-aligned via `auditTime(0, animationFrameScheduler)` so continuous
 * resize drags settle on the final frame instead of an intermediate one.
 */
class Fill extends AsyncDirective {
	private element: HTMLElement | null = null
	private disconnecting$ = new Subject<void>()

	private subscribe() {
		const el = this.element
		if (!el) return

		combineLatest([
			merge(
				fromEvent(window, 'resize', { passive: true }),
				window.visualViewport
					? merge(
							fromEvent(window.visualViewport, 'resize', { passive: true }),
							fromEvent(window.visualViewport, 'scroll', { passive: true }),
						)
					: EMPTY,
				fromEvent(window, 'orientationchange'),
				fromEvent(document, 'focusout', { passive: true }).pipe(switchMap(() => timer(100))),
				new Observable<{ observer: ResizeObserver; observed: Set<Element> }>(subscriber => {
					const observed = new Set<Element>([el])
					const observer = new ResizeObserver(() => subscriber.next({ observer, observed }))
					observer.observe(el)
					return () => observer.disconnect()
				}).pipe(
					auditTime(0, animationFrameScheduler),
					tap(({ observer, observed }) => {
						// Lit subscribes before its template attaches. Discover flattened
						// ancestors after layout, outside native RO delivery: observing
						// shallower boxes during delivery triggers a resize-loop error.
						let node: Element | null = el
						while (node) {
							if (!observed.has(node)) {
								observed.add(node)
								observer.observe(node)
							}
							const root = node.getRootNode()
							node = node.assignedSlot ?? node.parentElement ?? (root instanceof ShadowRoot ? root.host : null)
						}
					}),
				),
			).pipe(auditTime(0, animationFrameScheduler), startWith(null)),
			theme.bottomOffset$,
			theme.fullscreen$,
		])
			.pipe(
				filter(() => {
					const vv = window.visualViewport
					return vv ? Math.abs(vv.scale - 1) <= 0.01 : true
				}),
				map(([, bottomOffset, isFullscreen]) => {
					const vv = window.visualViewport
					const rect = el.getBoundingClientRect()
					const boundary = findFillBoundary(el.parentElement ?? el)
					const bRect = boundary?.getBoundingClientRect()
					const maxBottom = bRect ? bRect.bottom : (vv?.height ?? window.innerHeight)
					const maxRight = bRect ? bRect.right : (vv?.width ?? window.innerWidth)
					return {
						height: `${Math.max(0, maxBottom - rect.top)}px`,
						width: `${Math.max(0, maxRight - rect.left)}px`,
						paddingBottom: `${isFullscreen ? 0 : bottomOffset}px`,
					}
				}),
				// Idempotent gate: once geometry is stable the computed values
				// stop changing, so no DOM write happens and no resize is
				// emitted — the parent observer (and any ancestor's, e.g. the
				// overlay's) goes quiet instead of ping-ponging.
				distinctUntilChanged(
					(a, b) => a.height === b.height && a.width === b.width && a.paddingBottom === b.paddingBottom,
				),
				tap(({ height, width, paddingBottom }) => {
					const s = el.style
					s.boxSizing = 'border-box'
					s.height = height
					s.width = width
					s.paddingBottom = paddingBottom
					s.minHeight = '0'
					s.minWidth = '0'
					// Containment only — never scroll policy. An element that already
					// declares its own scrolling keeps it: this write recomputes on every
					// resize while `overflowWithin()` writes its longhands once per render,
					// so on a shared element `overflow: hidden` landed last and left a
					// scroller with no scrollbar and no wheel — yet still scrollable by
					// `scrollIntoView`, so every programmatic check read it as fine.
					if (s.overflowY !== 'auto' && s.overflowY !== 'scroll' && s.overflowX !== 'auto' && s.overflowX !== 'scroll') {
						s.overflowX = 'hidden'
						s.overflowY = 'hidden'
					}
				}),
				takeUntil(this.disconnecting$),
			)
			.subscribe()
	}

	render() {
		return
	}

	override update(part: ElementPart) {
		if (part.type !== PartType.ELEMENT) {
			throw new Error('fill directive can only be used on elements')
		}
		const el = part.element as HTMLElement
		if (this.element !== el) {
			this.element = el
			if (this.isConnected) this.subscribe()
		}
	}

	protected override disconnected() {
		this.disconnecting$.next()
	}

	protected override reconnected() {
		if (this.element) {
			this.disconnecting$ = new Subject<void>()
			this.subscribe()
		}
	}
}

export const fill = directive(Fill)
